import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  HiRocketLaunch,
  HiSparkles, 
  HiShieldCheck,
  HiChartBar,
  HiCog,
  HiUsers,
  HiGlobeAlt,
  HiArrowRight,
  HiArrowUpRight,
  HiPlay,
  HiStar,
  HiCloud,
  HiCpuChip,
  HiCodeBracket,
  HiCircleStack,
  HiDevicePhoneMobile,
  HiWrenchScrewdriver,
  HiEye,
  HiDocumentText,
  HiArrowTrendingUp,
  HiBolt,
  HiServer,
  HiServerStack,
  HiCommandLine,
  HiCog6Tooth,
  HiCog8Tooth,
  HiComputerDesktop,
  HiCube,
  HiLightBulb,
  HiCloudArrowUp,
  HiCloudArrowDown,
  HiChevronDown
} from 'react-icons/hi2';
import { IconWrapper } from '../components/common/IconWrapper';
import { caseStudies } from '../data/caseStudies';

const HomePage: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [activeDifferentiator, setActiveDifferentiator] = useState<string | null>(null);
  const [failedLogos, setFailedLogos] = useState<Set<string>>(new Set());

  const handleDifferentiatorToggle = (title: string) => {
    setActiveDifferentiator(prev => (prev === title ? null : title));
  };

  // Hero slides data focusing on Data Engineering, Analytics, and Cloud
  const heroSlides = [
    {
      id: 0,
      title: "Azure AI",
      subtitle: "Innovation",
      description: "Deploy responsible Azure AI copilots, cognitive search, and data agents across your enterprise with governed MLOps.",
      icon: HiCpuChip,
      gradient: "from-blue-600 to-blue-800",
      bgImage: "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=1600&q=80",
      features: ["Copilot Studio", "Azure OpenAI", "AI Studio", "Responsible AI"]
    },
    {
      id: 1,
      title: "Data Engineering",
      subtitle: "Excellence",
      description: "Transform raw data into actionable insights with advanced data pipelines, data lakes, and real-time processing solutions.",
      icon: HiCircleStack,
      gradient: "from-blue-600 to-blue-800",
      bgImage: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=1600&q=80",
      features: ["Data Lakes", "Delta Tables", "Streaming Apps", "Data Warehouses"]
    },
    {
      id: 2,
      title: "Data Analytics",
      subtitle: "Intelligence",
      description: "Unlock the power of your data with advanced analytics, business intelligence, and machine learning solutions.",
      icon: HiChartBar,
      gradient: "from-blue-600 to-blue-800",
      bgImage: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1600&q=80",
      features: ["Power BI", "Tableau", "Snowflake", "Python Analytics"]
    },
    {
      id: 3,
      title: "Cloud-First",
      subtitle: "Architecture",
      description: "Build scalable, secure, and cost-effective cloud solutions with AWS, Azure, and GCP expertise.",
      icon: HiCloud,
      gradient: "from-blue-600 to-blue-800",
      bgImage: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1600&q=80",
      features: ["AWS", "Azure", "GCP", "DataOps"]
    }
  ];

const techCategories = [
    {
      title: "Azure AI & Copilot Stack",
      description: "Blueprints for Azure OpenAI copilots, grounding, safety, and telemetry.",
      icon: HiCpuChip,
      technologies: ["Azure OpenAI", "Azure AI Studio", "AI Search", "Content Safety", "Power Platform", "Microsoft Fabric"]
    },
    {
      title: "Data Engineering Platforms",
      description: "Modern data engineering platforms for scalable data processing.",
      icon: HiCircleStack,
      technologies: ["Apache Spark", "Apache Kafka", "Apache Airflow", "Delta Lake"]
    },
    {
      title: "Cloud & Warehouses",
      description: "Leading cloud providers and lakehouse platforms for analytics.",
      icon: HiCloud,
      technologies: ["AWS", "Azure", "GCP", "Snowflake", "BigQuery", "Redshift"]
    },
    {
      title: "Analytics & BI Tools",
      description: "Advanced analytics and business intelligence solutions.",
      icon: HiChartBar,
      technologies: ["Power BI", "Tableau", "Looker", "Qlik", "dbt", "Python"]
    },
    {
      title: "Web & Mobile Experience Stack",
      description: "Composable web, native, and cross-platform delivery with shared design systems.",
      icon: HiComputerDesktop,
      technologies: ["Next.js", "Remix", "Tailwind CSS", "Swift", "Kotlin", "Flutter"]
    },
    {
      title: "DataOps & Automation",
      description: "Modern DataOps tools for data pipeline automation.",
      icon: HiCog6Tooth,
      technologies: ["Docker", "Kubernetes", "Terraform", "Jenkins", "GitHub Actions", "Azure DevOps"]
    }
  ];

  const differentiationCards = [
    {
      title: "Our Expertise ↗",
      description: "Industry-leading assessment, design, and development talent across AI, Cloud, and Data delivering where others fail.",
      link: "/services",
      accent: "from-blue-600/15 to-slate-900/10"
    },
    {
      title: "Our Engineering ↗",
      description: "High-performing agile squads and platform accelerators that turn complex ideas into production-grade products fast.",
      link: "/case-studies",
      accent: "from-blue-600/15 to-slate-900/10"
    },
    {
      title: "Our Experience ↗",
      description: "Deep domain knowledge across financial services, healthcare, retail, and manufacturing to unlock measurable outcomes.",
      link: "/solutions",
      accent: "from-blue-600/15 to-slate-900/10"
    },
    {
      title: "Our Partnership ↗",
      description: "Collaborative engagement, transparent governance, and 24/7 support that keep programs on track and stakeholders aligned.",
      link: "/contact",
      accent: "from-blue-600/15 to-slate-900/10"
    }
  ];

  const sectionTitleClass = "text-3xl md:text-4xl font-bold text-slate-900 dark:text-white";

  // Auto-advance slides
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, heroSlides.length]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 md:pt-0">
        {/* Background Image */}
        <AnimatePresence mode="sync">
          <motion.div
            key={currentSlide}
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
          >
            <img
              src={heroSlides[currentSlide].bgImage}
              alt={heroSlides[currentSlide].title}
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/60 to-slate-950/70" />
          </motion.div>
        </AnimatePresence>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Content */}
            <div className="text-center">

              <AnimatePresence mode="sync">
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 50 }}
                  transition={{ duration: 0.8 }}
                >
                  <motion.h1
                    className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-white drop-shadow-lg"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                  >
                    <span className={`bg-gradient-to-r ${heroSlides[currentSlide].gradient} bg-clip-text text-transparent`}>
                      {heroSlides[currentSlide].title}
                    </span>
                    <br />
                    <span className="text-white">
                      {heroSlides[currentSlide].subtitle}
                    </span>
                  </motion.h1>

                  <motion.p
                    className="text-xl md:text-2xl text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed drop-shadow-md"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                  >
                    {heroSlides[currentSlide].description}
                  </motion.p>

                  {/* Feature Tags */}
                  <motion.div
                    className="flex flex-wrap gap-3 mb-8 justify-center"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                  >
                    {heroSlides[currentSlide].features.map((feature, index) => (
                      <motion.span
                        key={index}
                        className="px-4 py-2 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm rounded-full text-sm font-medium text-slate-700 dark:text-slate-300 border border-white/30 dark:border-slate-700/50"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                        whileHover={{ scale: 1.05 }}
                      >
                        {feature}
                      </motion.span>
                    ))}
                  </motion.div>
                </motion.div>
              </AnimatePresence>

              <motion.div
                className="flex flex-col sm:flex-row gap-4 justify-center items-center"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
              >
                <Link to="/contact">
                  <motion.button
                    className="group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-800 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden w-full"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span className="relative z-10 flex items-center">
                      Get Started
                      <IconWrapper icon={HiArrowRight} className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-700 to-blue-900 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </motion.button>
                </Link>

                <motion.button
                  className="group flex items-center px-8 py-4 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm text-slate-700 dark:text-slate-300 font-semibold rounded-2xl border border-white/30 dark:border-slate-700/50 hover:bg-white dark:hover:bg-slate-800 transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <IconWrapper icon={HiPlay} className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                  Watch Demo
                </motion.button>
              </motion.div>

              {/* Slide Indicators */}
              <div className="flex justify-center mt-12 space-x-3">
                {heroSlides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setCurrentSlide(index);
                      setIsAutoPlaying(false);
                      setTimeout(() => setIsAutoPlaying(true), 10000);
                    }}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === currentSlide 
                        ? 'bg-white scale-125' 
                        : 'bg-white/50 hover:bg-white/70'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 bg-slate-50 dark:bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <motion.div
              className="text-left"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <motion.div
              className="inline-flex items-center px-8 py-4 rounded-full bg-blue-500/10 backdrop-blur-sm border border-blue-200/40 dark:border-blue-800/40 mb-6"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05, rotate: 2 }}
              >
                <IconWrapper icon={HiSparkles} className="w-6 h-6 text-blue-600 dark:text-blue-400 mr-3" />
                <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                  Why Choose FirstLogic?
                </span>
              </motion.div>

              <motion.h2
                className={`${sectionTitleClass} mb-8`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <span className="block text-slate-900 dark:text-white">
                  Why Choose
                </span>
                <span className="block text-blue-600 dark:text-blue-300">
                  FirstLogic?
                </span>
              </motion.h2>

              <motion.p
                className="text-xl md:text-2xl text-slate-600 dark:text-slate-300 max-w-2xl leading-relaxed"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                viewport={{ once: true }}
              >
                We combine cutting-edge technology with deep industry expertise to deliver solutions that drive
                real business value and accelerate your digital transformation.
              </motion.p>
            </motion.div>

            <motion.div
              className="grid sm:grid-cols-2 gap-6 items-start"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              {differentiationCards.map((card, index) => (
                <motion.div
                  key={card.title}
                  className="relative rounded-3xl bg-white dark:bg-slate-900 p-6 shadow-xl border border-slate-200 dark:border-slate-800 overflow-hidden"
                  whileHover={{ y: -5, scale: 1.02 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 20 }}
                >
                  <div className={`absolute inset-0 opacity-40 bg-gradient-to-br ${card.accent}`} />
                  <div className="relative z-10 flex flex-col">
                    <button
                      className="w-full flex items-center justify-between text-left text-sm font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wide"
                      onClick={() => handleDifferentiatorToggle(card.title)}
                    >
                      <span>{card.title}</span>
                      <IconWrapper
                        icon={HiChevronDown}
                        className={`w-4 h-4 transition-transform ${activeDifferentiator === card.title ? 'rotate-180' : ''}`}
                      />
                    </button>
                    <motion.div
                      initial={false}
                      animate={{
                        height: activeDifferentiator === card.title ? 'auto' : 0,
                        opacity: activeDifferentiator === card.title ? 1 : 0
                      }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed mt-4">
                        {card.description}
                      </p>
                    </motion.div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>

          <div className="mt-8" />
        </div>
      </section>

      {/* Services Section - Minimal palette */}
      <section className="py-16 relative overflow-hidden bg-slate-100 dark:bg-slate-900">
        <div className="absolute inset-0 bg-white/60 dark:bg-black/40" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <motion.h2
              className={`${sectionTitleClass} mb-8`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <span className="text-slate-900 dark:text-white">Our </span>
              <span className="text-blue-600 dark:text-blue-300">Services</span>
            </motion.h2>

            <motion.p
              className="text-xl md:text-2xl text-slate-600 dark:text-blue-100 max-w-4xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
            >
              Comprehensive technology solutions to accelerate your digital transformation journey 
              and drive innovation across your organization.
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: HiCpuChip,
                title: "Agentic AI",
                description: "Build and deploy AI applications with Azure Foundry and Azure OpenAI for intelligent business solutions.",
                features: ["Azure Foundry", "Azure OpenAI", "AI Deployment", "Responsible AI"],
                color: "from-blue-600 to-slate-900",
                bgColor: "from-white/40 to-slate-200/40 dark:from-slate-900/40 dark:to-slate-800/40",
                stats: "AI in Production",
                benefits: ["Azure Foundry", "Azure OpenAI"],
                link: "/services"
              },
              {
                icon: HiCircleStack,
                title: "Data Engineering & Analytics",
                description: "Complete data engineering services from data warehouses to ETL pipelines and BI reporting with Power BI.",
                features: ["Data Warehouses", "ETL Pipelines", "Power BI", "Data Lakes"],
                color: "from-blue-600 to-slate-900",
                bgColor: "from-white/40 to-slate-200/40 dark:from-slate-900/40 dark:to-slate-800/40",
                stats: "Real-time Processing",
                benefits: ["Data Warehouse", "ETL", "BI Reporting"],
                link: "/services"
              },
              {
                icon: HiCodeBracket,
                title: "Application Engineering",
                description: "Build modern applications and modernize legacy systems with cutting-edge technologies and best practices.",
                features: ["Application Development", "Application Modernization", "Full-stack", "Microservices"],
                color: "from-blue-600 to-slate-900",
                bgColor: "from-white/40 to-slate-200/40 dark:from-slate-900/40 dark:to-slate-800/40",
                stats: "Launch in 12 Weeks",
                benefits: ["Development", "Modernization"],
                link: "/services"
              },
              {
                icon: HiCloud,
                title: "Cloud Data Services",
                description: "Comprehensive cloud services with Azure, GCP, and AWS for infrastructure, data, and applications.",
                features: ["Azure", "GCP", "AWS", "Cloud Infrastructure"],
                color: "from-blue-600 to-slate-900",
                bgColor: "from-white/40 to-slate-200/40 dark:from-slate-900/40 dark:to-slate-800/40",
                stats: "Cloud-Native",
                benefits: ["Azure", "GCP", "AWS"],
                link: "/services"
              },
              {
                icon: HiServer,
                title: "DevOps",
                description: "Streamline your development and operations with automated CI/CD pipelines and infrastructure as code.",
                features: ["CI/CD Pipelines", "Infrastructure as Code", "Automation", "Monitoring"],
                color: "from-blue-600 to-slate-900",
                bgColor: "from-white/40 to-slate-200/40 dark:from-slate-900/40 dark:to-slate-800/40",
                stats: "80% Faster Deployments",
                benefits: ["CI/CD", "Automation"],
                link: "/services"
              },
              {
                icon: HiWrenchScrewdriver,
                title: "Application Modernization",
                description: "Transform legacy systems into modern, scalable applications with cloud-native architectures and microservices.",
                features: ["Legacy Migration", "Cloud Migration", "Microservices", "API Modernization"],
                color: "from-blue-600 to-slate-900",
                bgColor: "from-white/40 to-slate-200/40 dark:from-slate-900/40 dark:to-slate-800/40",
                stats: "Modern Architecture",
                benefits: ["Legacy Migration", "Cloud Native"],
                link: "/services/application-modernization"
              }
            ].map((service, index) => (
              <motion.div
                key={index}
                className="group relative"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
              >
                <Link to={service.link || '/services'} className="block">
                  <div className="relative p-8 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-lg overflow-hidden h-full cursor-pointer hover:shadow-xl transition-shadow">
                    <div className={`absolute inset-0 bg-gradient-to-br ${service.bgColor}`} />
                    <div className="relative z-10 flex flex-col h-full">
                      <div className={`inline-flex p-4 rounded-xl bg-gradient-to-r ${service.color} text-white w-max mb-5`}>
                        <IconWrapper icon={service.icon} className="w-8 h-8 text-white" />
                      </div>

                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-white text-xs font-semibold w-max mb-4">
                          {service.stats}
                        </span>

                      <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                        {service.title}
                      </h3>

                      <p className="text-slate-600 dark:text-slate-200 leading-relaxed mb-6">
                        {service.description}
                      </p>

                      <div className="space-y-2 mb-6">
                        {service.features.map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-center text-sm text-slate-600 dark:text-slate-300">
                            <span className="w-2 h-2 rounded-full bg-blue-600 mr-3" />
                            {feature}
                          </div>
                        ))}
                      </div>

                      <div className="mt-auto border-t border-slate-200 dark:border-slate-700 pt-4">
                        <div className="flex flex-wrap gap-2">
                          {service.benefits.map((benefit, benefitIndex) => (
                            <span
                              key={benefitIndex}
                              className="px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
                            >
                              {benefit}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Bottom CTA - Card Stack Style */}
          <motion.div
            className="text-center mt-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            viewport={{ once: true }}
          >
            <motion.button
              className="group inline-flex items-center justify-center px-8 py-4 bg-blue-600 text-white font-semibold rounded-xl shadow-lg hover:bg-blue-700 transition-colors"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
                Explore All Services
                <IconWrapper icon={HiArrowRight} className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Our Process Section */}
      <section className="py-16 relative bg-gradient-to-b from-white via-slate-50 to-white dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-20 dark:opacity-40">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.12),_transparent_55%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom,_rgba(15,23,42,0.2),_transparent_65%)]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="md:flex md:items-end md:justify-between mb-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
              <p className="text-xs font-semibold uppercase tracking-[0.4em] text-blue-600 mb-3">
                Waveflow
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">
                Our Process
            </h2>
              <p className="text-lg text-slate-600 dark:text-slate-300 max-w-2xl mt-3">
                A proven cascade of collaborative steps that move from requirements to adoption, designed to keep momentum and clarity at every stage.
            </p>
          </motion.div>
            <div className="hidden md:flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
              <div className="w-2 h-2 rounded-full bg-blue-600" /> Continuous engagement
          </div>
              </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            {[
              {
                step: "1",
                title: "Requirement Gathering",
                description: "Stakeholder interviews and workshops to capture goals, constraints, and success metrics.",
                accent: "from-blue-500 to-blue-700",
                icon: HiEye
              },
              {
                step: "2",
                title: "Requirement Analysis",
                description: "Map dependencies, prioritize capabilities, and architect the reference solution.",
                accent: "from-slate-800 to-slate-900",
                icon: HiDocumentText
              },
              {
                step: "3",
                title: "Proof of Concept",
                description: "Rapid experimentation to validate feasibility, demonstrate value, and refine the backlog.",
                accent: "from-blue-700 to-indigo-700",
                icon: HiCodeBracket
              },
              {
                step: "4",
                title: "Application Delivery",
                description: "Iterative build, DataOps automation, and production rollout with guardrails.",
                accent: "from-slate-900 to-slate-800",
                icon: HiCog
              }
            ].map((phase, index) => (
                <motion.div
                key={phase.step}
                className="relative group rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-lg overflow-hidden"
                initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
              >
                <div className={`absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r ${phase.accent}`} />
                <div className="flex items-center justify-between mb-6">
                  <div className="text-sm font-semibold tracking-[0.3em] text-slate-400">
                    {phase.step.padStart(2, "0")}
                      </div>
                  <div className="w-11 h-11 rounded-2xl bg-blue-50 dark:bg-slate-800 flex items-center justify-center text-blue-600 dark:text-blue-300">
                    <IconWrapper icon={phase.icon} className="w-5 h-5" />
                    </div>
                  </div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                  {phase.title}
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                  {phase.description}
                </p>
                {index < 3 && (
                  <div className="absolute right-[-12px] top-1/2 transform -translate-y-1/2 hidden md:block">
                    <div className="w-6 h-0.5 bg-gradient-to-r from-slate-300 to-transparent dark:from-slate-700" />
                        </div>
                )}
                      </motion.div>
                    ))}
          </div>
        </div>
      </section>

      {/* Tech Stack Section - Logos Only */}
      <section className="py-8 relative overflow-hidden bg-white dark:bg-slate-950">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-6"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <motion.h2
              className={`${sectionTitleClass} mb-4`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <span>Cutting-Edge Technologies</span>
            </motion.h2>
          </motion.div>

          {/* Technology Logos Grid */}
          <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
            >
            <div className="p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-lg">
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-4">
              {(() => {
                // Technology logo mapping with multiple fallback sources
                // Only include technologies with reliable logo sources
                const logoMap: Record<string, string[]> = {
                    'Azure OpenAI': [
                      'https://cdn.simpleicons.org/microsoftazure/0078D4',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/microsoftazure/microsoftazure-original.svg',
                      'https://img.icons8.com/color/96/000000/azure-1.png'
                    ],
                    'Azure AI Studio': [
                      'https://cdn.simpleicons.org/microsoftazure/0078D4',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/microsoftazure/microsoftazure-original.svg'
                    ],
                    'AI Search': [
                      'https://cdn.simpleicons.org/microsoftazure/0078D4',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/microsoftazure/microsoftazure-original.svg'
                    ],
                    'Content Safety': [
                      'https://cdn.simpleicons.org/microsoftazure/0078D4',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/microsoftazure/microsoftazure-original.svg'
                    ],
                    'Power Platform': [
                      'https://cdn.simpleicons.org/microsoftpowerplatform/0078D4',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/microsoftazure/microsoftazure-original.svg'
                    ],
                    'Microsoft Fabric': [
                      'https://cdn.simpleicons.org/microsoftazure/0078D4',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/microsoftazure/microsoftazure-original.svg'
                    ],
                    'Apache Spark': [
                      'https://cdn.simpleicons.org/apachespark/E25A1C',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/apachespark/apachespark-original.svg',
                      'https://img.icons8.com/color/96/000000/apache-spark.png'
                    ],
                    'Apache Kafka': [
                      'https://cdn.simpleicons.org/apachekafka/231F20',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/apachekafka/apachekafka-original.svg',
                      'https://img.icons8.com/color/96/000000/apache-kafka.png'
                    ],
                    'Apache Airflow': [
                      'https://cdn.simpleicons.org/apacheairflow/017CEE',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/apache/apache-original.svg',
                      'https://img.icons8.com/color/96/000000/apache-airflow.png'
                    ],
                    'Delta Lake': [
                      'https://cdn.simpleicons.org/deltaio/00ADD8',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/deltaio/deltaio-original.svg'
                    ],
                    'AWS': [
                      'https://cdn.simpleicons.org/amazonaws/232F3E',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/amazonwebservices/amazonwebservices-original.svg',
                      'https://img.icons8.com/color/96/000000/amazon-web-services.png'
                    ],
                    'Azure': [
                      'https://cdn.simpleicons.org/microsoftazure/0078D4',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/microsoftazure/microsoftazure-original.svg',
                      'https://img.icons8.com/color/96/000000/azure-1.png'
                    ],
                    'GCP': [
                      'https://cdn.simpleicons.org/googlecloud/4285F4',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/googlecloud/googlecloud-original.svg',
                      'https://img.icons8.com/color/96/000000/google-cloud.png'
                    ],
                    'Snowflake': [
                      'https://cdn.simpleicons.org/snowflake/29B5E8',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/snowflake/snowflake-original.svg',
                      'https://img.icons8.com/color/96/000000/snowflake.png'
                    ],
                    'BigQuery': [
                      'https://cdn.simpleicons.org/googlecloud/4285F4',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/googlecloud/googlecloud-original.svg',
                      'https://img.icons8.com/color/96/000000/google-cloud.png'
                    ],
                    'Redshift': [
                      'https://cdn.simpleicons.org/amazonaws/232F3E',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/amazonwebservices/amazonwebservices-original.svg',
                      'https://img.icons8.com/color/96/000000/amazon-web-services.png'
                    ],
                    'Power BI': [
                      'https://cdn.simpleicons.org/powerbi/F2C811',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/powerbi/powerbi-original.svg',
                      'https://img.icons8.com/color/96/000000/power-bi.png'
                    ],
                    'Tableau': [
                      'https://cdn.simpleicons.org/tableau/E97627',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/tableau/tableau-original.svg',
                      'https://img.icons8.com/color/96/000000/tableau-software.png'
                    ],
                    'Looker': [
                      'https://cdn.simpleicons.org/googlecloud/4285F4',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/googlecloud/googlecloud-original.svg',
                      'https://img.icons8.com/color/96/000000/google-cloud.png'
                    ],
                    'Qlik': [
                      'https://cdn.simpleicons.org/qlik/00A0DC',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/qlik/qlik-original.svg',
                      'https://img.icons8.com/color/96/000000/qlik.png'
                    ],
                    'dbt': [
                      'https://cdn.simpleicons.org/dbt/FF694B',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/dbt/dbt-original.svg',
                      'https://img.icons8.com/color/96/000000/database.png'
                    ],
                    'Python': [
                      'https://cdn.simpleicons.org/python/3776AB',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/python/python-original.svg',
                      'https://img.icons8.com/color/96/000000/python.png'
                    ],
                    'Next.js': [
                      'https://cdn.simpleicons.org/nextdotjs/000000',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/nextjs/nextjs-original.svg',
                      'https://img.icons8.com/color/96/000000/nextjs.png'
                    ],
                    'Remix': [
                      'https://cdn.simpleicons.org/remix/000000',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/react/react-original.svg',
                      'https://img.icons8.com/color/96/000000/react-native.png'
                    ],
                    'Tailwind CSS': [
                      'https://cdn.simpleicons.org/tailwindcss/06B6D4',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/tailwindcss/tailwindcss-original.svg',
                      'https://img.icons8.com/color/96/000000/tailwindcss.png'
                    ],
                    'Swift': [
                      'https://cdn.simpleicons.org/swift/FA7343',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/swift/swift-original.svg',
                      'https://img.icons8.com/color/96/000000/swift.png'
                    ],
                    'Kotlin': [
                      'https://cdn.simpleicons.org/kotlin/7F52FF',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/kotlin/kotlin-original.svg',
                      'https://img.icons8.com/color/96/000000/kotlin.png'
                    ],
                    'Flutter': [
                      'https://cdn.simpleicons.org/flutter/02569B',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/flutter/flutter-original.svg',
                      'https://img.icons8.com/color/96/000000/flutter.png'
                    ],
                    'Docker': [
                      'https://cdn.simpleicons.org/docker/2496ED',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/docker/docker-original.svg',
                      'https://img.icons8.com/color/96/000000/docker.png'
                    ],
                    'Kubernetes': [
                      'https://cdn.simpleicons.org/kubernetes/326CE5',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/kubernetes/kubernetes-plain.svg',
                      'https://img.icons8.com/color/96/000000/kubernetes.png'
                    ],
                    'Terraform': [
                      'https://cdn.simpleicons.org/terraform/7B42BC',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/terraform/terraform-original.svg',
                      'https://img.icons8.com/color/96/000000/terraform.png'
                    ],
                    'Jenkins': [
                      'https://cdn.simpleicons.org/jenkins/D24939',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/jenkins/jenkins-original.svg',
                      'https://img.icons8.com/color/96/000000/jenkins.png'
                    ],
                    'GitHub Actions': [
                      'https://cdn.simpleicons.org/githubactions/2088FF',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/github/github-original.svg',
                      'https://img.icons8.com/color/96/000000/github.png'
                    ],
                    'Azure DevOps': [
                      'https://cdn.simpleicons.org/microsoftazure/0078D4',
                      'https://raw.githubusercontent.com/devicons/devicon/master/icons/microsoftazure/microsoftazure-original.svg',
                      'https://img.icons8.com/color/96/000000/azure-1.png'
                    ],
                  };

                // Filter technologies to only show those with logos defined and not failed
                const allTechs = Array.from(new Set(techCategories.flatMap(category => category.technologies)));
                const techsWithLogos = allTechs.filter(tech => 
                  logoMap[tech] && 
                  logoMap[tech].length > 0 && 
                  !failedLogos.has(tech)
                );

                return techsWithLogos.map((tech) => {
                const logoUrls = logoMap[tech];
                const primaryLogo = logoUrls[0];

                return (
                      <motion.div
                    key={tech}
                    className="motion-div-card group relative flex flex-col items-center justify-center"
                    whileHover={{ scale: 1.15 }}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                      viewport={{ once: true }}
                  >
                    <div className="w-14 h-14 rounded-lg overflow-hidden bg-transparent flex items-center justify-center">
                      <img
                        src={primaryLogo}
                        alt={tech}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          const parent = target.parentElement;
                          if (parent && logoUrls.length > 1) {
                            let currentIndex = 0;
                            for (let i = 0; i < logoUrls.length; i++) {
                              if (target.src === logoUrls[i]) {
                                currentIndex = i;
                                break;
                              }
                            }
                            if (currentIndex < logoUrls.length - 1) {
                              target.src = logoUrls[currentIndex + 1];
                              return;
                            }
                          }
                          // Mark this technology as failed and hide the card
                          setFailedLogos(prev => new Set(prev).add(tech));
                          const cardElement = target.closest('.motion-div-card');
                          if (cardElement) {
                            (cardElement as HTMLElement).style.display = 'none';
                          }
                        }}
                      />
                    </div>
                    {/* Tooltip on hover */}
                    <div className="absolute bottom-full mb-2 px-3 py-1.5 bg-slate-900 dark:bg-slate-800 text-white text-xs font-medium rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                      {tech}
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                        <div className="w-2 h-2 bg-slate-900 dark:bg-slate-800 rotate-45"></div>
                  </div>
                </div>
              </motion.div>
                );
                });
              })()}
          </div>
        </div>
          </motion.div>
        </div>
      </section>


      {/* Case Studies */}
      <section className="py-12 bg-white dark:bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-10"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-6">
              Case Studies
            </h2>
            <p className="text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
              Real-world transformations that delivered measurable business impact.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {caseStudies.slice(0, 3).map((study, index) => (
              <motion.div
                key={study.id}
                className="h-[400px] perspective-1000 flip-card-container"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="relative w-full h-full flip-card-inner">
                  {/* Front Side - Image + Title */}
                  <div className="absolute inset-0 w-full h-full backface-hidden rounded-3xl overflow-hidden bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-sm">
                    <div className="relative h-full w-full">
                      <img
                        src={study.image}
                      alt={study.title}
                      className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <p className="text-xs font-semibold text-white/90 mb-2">Case Study</p>
                        <h3 className="text-xl font-bold text-white mb-2">{study.title}</h3>
                        <p className="text-sm text-white/90 mb-3">{study.client}</p>
                        <div className="flex items-center justify-center">
                          <IconWrapper icon={HiArrowRight} className="w-6 h-6 text-white/80" />
                  </div>
                      </div>
                  </div>
                      </div>

                  {/* Back Side - Details */}
                  <div className="absolute inset-0 w-full h-full backface-hidden rotate-y-180 rounded-3xl overflow-hidden bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-sm">
                    <div className="h-full p-6 overflow-y-auto">
                      <div className="space-y-4 text-sm text-slate-600 dark:text-slate-300">
                        <div>
                          <p className="font-semibold text-slate-900 dark:text-white mb-1">Challenge</p>
                          <p>{study.challenge}</p>
                      </div>
                        <div>
                          <p className="font-semibold text-slate-900 dark:text-white mb-1">Solution</p>
                          <p>{study.solution}</p>
                      </div>
                        <div>
                          <p className="font-semibold text-slate-900 dark:text-white mb-1">Results</p>
                          <ul className="space-y-1">
                            {study.results.map((result, idx) => (
                              <li key={idx} className="flex items-center">
                                <span className="w-2 h-2 rounded-full bg-blue-600 mr-2" />
                                {result.metric}: {result.value}
                              </li>
                        ))}
                      </ul>
                  </div>
                </div>
          </div>
        </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>





      {/* CTA Section */}
      <section className="py-12 bg-white dark:bg-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="border border-slate-200 dark:border-slate-800 rounded-3xl p-10 shadow-sm">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
                Ready to Transform Your Data?
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-300 mb-8">
                Join thousands of companies already using our data engineering and analytics solutions to drive growth and innovation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="px-8 py-4 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-colors text-center">
                  Start Free Trial
              </Link>
              <button className="px-8 py-4 border border-slate-300 dark:border-slate-700 rounded-full font-semibold text-slate-700 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                  Schedule Demo
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;